// deps
import Router from "next/router";

// internal deps
import { NB_PLATFORM, NativeBridge, NativeRequestType, NativeResponseType, init } from "@swiggy-private/native-bridge";
import {
    CheckoutMetaInfoResponsePayload,
    NativeResponsePayload,
    NativeRequest,
    NativeRequestPayload,
} from "@swiggy-private/native-bridge/dist/interfaces";
import { init as initWebSdk, STATUS_CODES, ERROR_MESSAGES } from "@swiggy-private/web-bridge-sdk";

// utils
import { isAndroid, isIOS } from "./utils";

export const getNativeBridgePlatform = (): NB_PLATFORM => {
    if (isAndroid()) {
        return NB_PLATFORM.ANDROID;
    }

    if (isIOS()) {
        return NB_PLATFORM.IOS;
    }

    return NB_PLATFORM.WEB;
};

const closeWebview = (payload: any) => {
    if (typeof window === "undefined") {
        return Promise.reject({
            statusMessage: "Window not available",
            statusCode: 1,
        });
    }

    Router.replace("/cart/");
    return Promise.resolve({} as NativeResponseType);
};

const directPlaceOrderFailure = (payload: any) => {
    if (typeof window === "undefined") {
        return Promise.reject({
            statusMessage: "Window not available",
            statusCode: 1,
        });
    }

    Router.replace("/cart/?type=FAILURE");
    return Promise.resolve({} as NativeResponseType);
};

const getCheckoutMetaInfo = (payload: any): Promise<CheckoutMetaInfoResponsePayload> => {
    if (typeof window === "undefined") {
        return Promise.reject({
            statusMessage: "Window not available",
            statusCode: 1,
        });
    }

    const checkoutMetaInfo = {
        cartItemsCount: Number(sessionStorage.getItem("items-quantity")) || 1,
        discountMessage: sessionStorage.getItem("discount-message") || "",
    };
    return Promise.resolve(checkoutMetaInfo);
};

export default class WebBridgeSdk extends NativeBridge {
    private static instance: WebBridgeSdk | null = null;

    private constructor() {
        super(getNativeBridgePlatform());
    }

    public static getInstance = (): NativeBridge | undefined =>
        WebBridgeSdk.instance != null ? WebBridgeSdk.instance : undefined;

    private initializeWebSdk() {
        initWebSdk(this.requestHandler);
        init({ platform: getNativeBridgePlatform() });
    }

    public static setUpWebSdk() {
        if (WebBridgeSdk.instance != null || typeof window == "undefined") {
            return;
        }

        WebBridgeSdk.instance = new WebBridgeSdk();
        WebBridgeSdk.instance?.initializeWebSdk();
    }

    private requestHandler = (
        req: NativeRequest<NativeRequestPayload>,
    ): Promise<{
        payload: NativeResponsePayload;
        statusCode?: number;
    }> => {
        return this.doHandleRequest(req).then((payload) => ({ payload }));
    };

    private doHandleRequest = (req: NativeRequest<NativeRequestPayload>): Promise<NativeResponsePayload> => {
        switch (req.type) {
            case NativeRequestType.CLOSE_WEBVIEW:
                return closeWebview(req.payload);
            case NativeRequestType.DIRECT_PLACE_ORDER_FAILURE:
                return directPlaceOrderFailure(req.payload);
            case NativeRequestType.GET_CHECKOUT_META_INFO:
                return getCheckoutMetaInfo(req.payload);
        }

        return Promise.reject({
            statusCode: STATUS_CODES.NOT_HANDLED,
            statusMessage: ERROR_MESSAGES.PLUGIN_NOT_HANDLED,
        });
    };
}
